<script>
  import FilterControls from "$lib/components/FilterControls/FilterControls.svelte";
  import { scaleOrdinal } from "d3-scale";
  import {
    CHART_COLORS_GROUPED,
    CHART_COLORS_SIMPLE,
    CHART_COLORS_SUBCATS
  } from "$utils/colors.js";
  import VizLayout from "$lib/components/VizLayout/VizLayout.svelte";
  import { useToolData } from "$utils/data.svelte.js";
  import { formatYear, formatQuarter } from "$utils/dates.js";
  import { formatNumber } from "$utils/formats.js";
  import { LayerCake, Svg, Html, flatten, stack } from "layercake";
  import { AxisX, AxisY } from "@urbaninstitute/dataviz-components";
  import { fade } from "svelte/transition";
  import AreaStacked from "./AreaStacked.svelte";
  import AreaLine from "./AreaLine.svelte";
  import Quadtree from "./Quadtree.svelte";
  import Tooltip from "./Tooltip.svelte";
  import StackPoints from "./StackPoints.svelte";
  import { MEDICATION_NAMES } from "$utils/consts.js";

  const toolData = useToolData();
  const chartLeftMargin = 50;

  let chartWidth = $state();
  let chartHeight = $derived.by(getChartHeight);

  let currentLabels = $derived(
    toolData.activeCategories.map((cat) => toolData.formatCategory(cat))
  );
  let seriesColors = $derived(
    toolData.isBreakdownMode ? CHART_COLORS_SUBCATS : CHART_COLORS_SIMPLE
  );
  let groupedLabels = $derived([
    {
      group: "Brand",
      items: MEDICATION_NAMES.map((d) => d.label).filter((label) => label !== "All")
    },
    {
      group: "Generic",
      items: MEDICATION_NAMES.map((d) => d.label).filter((label) => label !== "All")
    }
  ]);

  function getChartHeight() {
    let ratio = 4 / 3;
    return chartWidth / ratio;
  }

  const yKey = [0, 1];
  const zKey = "key";
  const xKey = "date";
  let seriesNames = $derived(toolData.activeCategories);

  let winWidth = $state(0);
  let numTicks = $derived.by(() => (winWidth < 768 ? 6 : 8));

  let formatCat = "default";

  let formatLabelX = $derived(toolData.activeFrequency === "annual" ? formatYear : formatQuarter);
  let formatLabelY = $derived((d) => formatNumber(d, toolData.activeFormat, formatCat));

  let stackedData = $derived(
    toolData.chartData.length > 0 ? stack(toolData.chartData, seriesNames.toReversed()) : []
  );

  let flatData = $derived(flatten(stackedData));

  let maxValue = $derived.by(getMaxValue);
  let yDomain = $derived([0, getRoundedMax(maxValue)]);

  let tooltipActive = $state(false);
  let activeHighlight = $state();
  let activeGroup = $state();

  function getMaxValue() {
    if (stackedData.length === 0) return 0;
    let rawMax = Math.max(...flatData.map((d) => (d[1] ? d[1] : 0)));
    return getRoundedMax(rawMax);
  }

  function getRoundedMax(rawMax) {
    return rawMax * 1;
  }
</script>

<svelte:window bind:innerWidth={winWidth} />
<VizLayout>
  {#snippet controls(onClose)}
    <FilterControls {onClose} />
  {/snippet}
  {#snippet viz()}
    <div>
      <div class="legend-holder">
        {#if toolData.activeMedType == "all"}
          {#each groupedLabels as group, i}
            <div class="legend">
              <h6 class="legend-title">{group.group}</h6>
              {#each group.items as label, j (label)}
                {@const color = CHART_COLORS_GROUPED[i][j]}
                <div class="legend-item">
                  <div class="swatch" style={`background-color: ${color};`}></div>
                  <span class="legend-label">{label}</span>
                </div>
              {/each}
            </div>
          {/each}
        {:else}
          <div class="legend">
            <h6 class="legend-title">
              {toolData.activeMedType}
            </h6>
            {#each currentLabels as label, i (label)}
              {@const color = seriesColors[i]}
              {@const category = toolData.activeCategories[i]}
              <div class="legend-item">
                <div class="swatch" style={`background-color: ${color};`}></div>
                <span class="legend-label">{label}</span>
              </div>
            {/each}
          </div>
        {/if}
      </div>
      <div
        class="chart-container"
        bind:clientWidth={chartWidth}
        style:--chart-height="{chartHeight}px"
        transition:fade={{ duration: 1000, delay: 200 }}
      >
        {#if toolData.chartData.length > 0}
          <LayerCake
            padding={{ top: 0, right: 0, bottom: 20, left: chartLeftMargin }}
            custom={{ valueFormat: formatLabelY, xFormat: formatLabelX }}
            x={(d) => d.data[xKey]}
            {yDomain}
            y={yKey}
            z={zKey}
            zScale={scaleOrdinal()}
            zDomain={seriesNames}
            zRange={seriesColors}
            {flatData}
            data={stackedData}
          >
            <Svg>
              <defs>
                <clipPath id="chartArea">
                  <rect x="0" y="0" width="100%" height="100%" />
                </clipPath>
              </defs>
              <AxisX formatTick={formatYear} tickMarks ticks={toolData.ticks} />
              <AxisY formatTick={formatLabelY} ticks={numTicks} />
              <g class="chart-area" clip-path="url(#chartArea)">
                <AreaStacked
                  bind:activeGroup
                  opacity={activeHighlight ? 0.8 : 1}
                  highlight={toolData.highlightCategory}
                />
                <AreaLine />
              </g>
              ></Svg
            >
            <Html>
              <Quadtree dataset={toolData.chartData} y="x" bind:activeHighlight>
                {#snippet children(props)}
                  {#if props.found && props.visible}
                    <Tooltip
                      title={formatLabelX(props.found.date)}
                      leftOffset={chartLeftMargin}
                      props={props.found}
                      description={toolData.getMetricLabel(toolData.activeMetric)}
                      x={props.x}
                      keys={seriesNames}
                      keyFormat={(k) => toolData.formatCategory(k)}
                    />
                  {/if}
                {/snippet}
              </Quadtree>
            </Html>
            <Svg pointerEvents={false}>
              <StackPoints filter={activeHighlight} />
            </Svg>
          </LayerCake>
        {/if}
      </div>
    </div>
  {/snippet}
</VizLayout>

<style>
  .chart-container {
    width: 100%;
    height: var(--chart-height);
    margin-top: var(--spacing-8);
  }
  .swatch {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    flex-grow: 0;
  }
  .legend-holder {
    display: flex;
    gap: 42px;
  }
  .legend {
    margin-block: var(--spacing-4);
    max-width: 600px;
    min-height: 48px;
  }
  .legend-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    font-size: var(--font-size-sm);
  }
  .legend h6.legend-title {
    font-family: var(--font-family-sans-alt);
    margin: 0px;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 40px; /* 285.714% */
    letter-spacing: 1.4px;
    text-transform: uppercase;
  }

  @media (min-width: 768px) {
    .chart-container {
      margin-top: var(--spacing-16);
      margin-bottom: var(--spacing-16);
    }
  }
</style>
